import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import Images from "../../../utils/Images";
import { ArrowLeft } from "lucide-react";
import authService from "../../../Services/AuthService";
import { toast } from "react-toastify";
import { loginSuccess } from "../../../store/authSlice";

export default function SignIn() {
  const [method, setMethod] = useState("email");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Check for success message from verification
  useEffect(() => {
    if (location.state?.message) {
      toast.success(location.state.message);
    }
  }, [location.state]);

  const isValidIdentifier =
    (method === "email" && /.+@.+\..+/.test(identifier)) ||
    (method === "phone" && /^\+?\d{7,}$/.test(identifier));
  const canSubmit = isValidIdentifier && password.length >= 6;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || isLoading) return;
    
    setIsLoading(true);
    
    try {
      const payload = {
        email_or_phone: identifier,
        password: password,
      };
      const response = await authService.login(payload);
      if (response.status && response.user && response.token) {
        
        // CHECK IF AGENT ACCOUNT IS VERIFIED
        if (response.user.email_verified_at || response.user.phone_number_verified_at) {
          dispatch(loginSuccess({
            user: response.user,
            token: response.token
          }));
          toast.success(response.message || "Login Successful");
          const from = location.state?.from?.pathname;
          navigate(from && from !== "/signin" ? from : "/dashboard", { replace: true });
        } else {
          // Redirect to verification page with user data
          navigate("/verify", {
            state: {
              email: response.user.email,
              phone: response.user.phone_number,
              slug: response.user.slug,
              message: "Please verify your account to continue.",
            },
          });
        }
      } else {
        toast.error(response.message || "Login Failed");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("Login error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="overflow-hidden relative h-screen">
      <img
        src="https://images.unsplash.com/photo-1560264418-c4445382edbc?q=80&w=1470&auto=format&fit=crop"
        alt="bg"
        className="object-cover absolute inset-0 w-full h-full"
      />
      <div className="absolute inset-0 pointer-events-none bg-black/60" />

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex absolute top-0 right-0 left-0 z-10 justify-center pt-6"
      >
        <img src={Images.logo_navbar} alt="TripSecure" className="h-10" />
      </motion.div>
      <button onClick={() => navigate(-1)} className="flex absolute top-4 left-4 z-20 gap-2 items-center px-3 py-2 text-xs font-medium rounded-lg border backdrop-blur text-white/90 border-white/20 bg-white/10 hover:bg-white/20">
        <ArrowLeft size={14} /> Back
      </button>

      <div className="flex relative z-10 justify-center items-center p-6 h-screen">
        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="p-6 w-full max-w-md rounded-3xl shadow-2xl bg-white/95"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
          >
            <h1 className="text-xl font-bold text-center">Welcome back</h1>
            <p className="mt-1 text-xs text-center text-gray-500">
              Login with your email or phone
            </p>
          </motion.div>

          <div className="mt-6 space-y-4">
            <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => {
                  setMethod("email");
                  setIdentifier("");
                }}
                className={`px-3 py-2 text-sm rounded-md ${
                  method === "email"
                    ? "bg-white shadow font-semibold"
                    : "text-gray-600"
                }`}
              >
                Email
              </button>
              <button
                type="button"
                onClick={() => {
                  setMethod("phone");
                  setIdentifier("");
                }}
                className={`px-3 py-2 text-sm rounded-md ${
                  method === "phone"
                    ? "bg-white shadow font-semibold"
                    : "text-gray-600"
                }`}
              >
                Phone
              </button>
            </div>

            <div className="space-y-3">
              <input
                type={method === "email" ? "email" : "tel"}
                placeholder={
                  method === "email" ? "you@example.com" : "+233 555 123 456"
                }
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                className="px-4 py-3 w-full text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="px-4 py-3 w-full text-sm rounded-xl border border-gray-200 outline-none bg-white/95 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-100"
              />
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-emerald-600 hover:text-emerald-700"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <motion.button
              type="submit"
              disabled={!canSubmit || isLoading}
              whileHover={{ scale: canSubmit && !isLoading ? 1.02 : 1 }}
              whileTap={{ scale: canSubmit && !isLoading ? 0.98 : 1 }}
              className="overflow-hidden relative px-4 py-3 w-full text-sm font-semibold text-white bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl shadow-lg hover:from-emerald-600 hover:to-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-400/50 focus:ring-offset-2 disabled:opacity-50"
            >
              {isLoading ? (
                <div className="flex gap-2 justify-center items-center">
                  <div className="w-4 h-4 rounded-full border-2 animate-spin border-white/30 border-t-white"></div>
                  Signing In...
                </div>
              ) : (
                "Sign In"
              )}
            </motion.button>

            <p className="text-xs text-center text-gray-600">
              Don’t have an account?{" "}
              <Link
                to="/signup"
                className="text-emerald-600 hover:text-emerald-700"
              >
                Create one
              </Link>
            </p>
          </div>
        </motion.form>
      </div>
    </div>
  );
}
